import { ActionList, Button, Popover, Modal, TextContainer, TextStyle } from "@shopify/polaris";
import { checkRole, getParamByRole, reducerFn } from "../../../helper";
import React, { useState, useCallback } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { useAppContext } from "../../../context";
import { useHistory } from "react-router-dom";
import TiktokProductStore from "./tiktok-product-store";
const _ = require('lodash');


const DELETE_SELECTED_PUSHES = gql`
	mutation deleteSelectedTiktokPushes($ids: [ID!]) {
		deleteSelectedTiktokPushes(ids: $ids)
	}
`;
const PAUSE_SELECTED_PUSHES = gql`
	mutation pauseSelectedTiktokPushes($ids: [ID!]) {
		pauseSelectedTiktokPushes(ids: $ids)
	}
`;

const RESUME_SELECTED_PUSHES = gql`
	mutation resumeSelectedTiktokPushes($ids: [ID!]) {
		resumeSelectedTiktokPushes(ids: $ids)
	}
`;


const FORCE_GENERATE_TIKTOK_PUSH_EXCEL_FILE = gql`
	mutation forceGenerateTiktokPushExcelFile($tiktokPushID: ID!) {
		forceGenerateTiktokPushExcelFile(tiktokPushID: $tiktokPushID)
	}
`;

const TIKTOK_REPUSH_PRODUCT_PROCESS = gql`
	mutation tiktokRePushProductProcess($ids: [ID!]!) {
		tiktokRePushProductProcess(ids: $ids)
	}
`;

export default function PushActions({item, onActionCompleted}) {
	const history = useHistory();
	const [open, setOpen] = useState(false);
	const [actionInModal, setActionInModal] = useState({});
	const [modalActionLoading, setModalActionLoading] = useState(false);
	const [activeRow, setActiveRow] = useState(null);
	const [state, setState] = React.useReducer(reducerFn, {
		openDetail: false,
	})

	const { currentUser } = useAppContext();
	const { isSeller } = checkRole(currentUser)
	const param = getParamByRole(currentUser);

	const handleAction = (action, item) => {
		switch(action) {
			case 'edit':
				let itemDetailUrl = `/${param}/tiktok-hub/push-products/${item?.id}`;
				history.push(itemDetailUrl);
				break
			case 'pause':
			case 'resume':
			case 'reGenrate':
			case 'rePush':
			case 'delete':
				setOpen(true);
				setActionInModal({
					action,
					item
				});
				break;
		}
		setActiveRow(null); // Close the popover
	};

	const handleOpenDetail = React.useCallback((value) => () => {
		setState({ openDetail: value })
	}, [])

	const renderActions = useCallback((item) => {
		let actions = [{
			content: 'Edit push',
			onAction: () => handleAction('edit', item),
		}];

		if (isSeller || isAuthor(currentUser, item)) {
			actions.unshift({
				content: 'View Detail',
				onAction: handleOpenDetail(true),
			});
		}

		if (item.status != 'Done' && !item.paused) {
			actions.push({
				content: 'Pause push',
				onAction: () => handleAction('pause', item),
			});
		}
		if (item.status != 'Done' && item.paused) {
			actions.push({
				content: 'Resume push',
				onAction: () => handleAction('resume', item),
			});
		}

		if (["Done", "Error"].includes(item.status) && item.processingMode === "EXPORT_EXCEL") {
			actions.push({
				content: 'Re-Generate Excel file',
				onAction: () => handleAction('reGenrate', item),
			});
		}

		if (isSeller || isAuthor(currentUser, item)) {
			actions.push({
				content: 'Push Again',
				onAction: () => handleAction('rePush', item),
			});
		}

		if (isSeller || isAuthor(currentUser, item)) {
			actions.push({
				content: 'Delete push',
				onAction: () => handleAction('delete', item),
			});
		}

		return (
			<Popover
				active={activeRow === item.id}
				activator={
					<Button disclosure onClick={() => {
						setActiveRow(prevActiveRow => {
							let newActiveRow = (prevActiveRow === item.id ? null : item.id);
							return newActiveRow;
						});
					}}>Actions</Button>
				}
				onClose={() => setActiveRow(null)}
			>
				<ActionList items={actions} />
			</Popover>
		)
	}, [activeRow]);

	const handleModalChange = useCallback(() => setOpen((open) => !open), []);

	const [deleteSelectedPushes, {}] = useMutation(DELETE_SELECTED_PUSHES);
	const [pauseSelectedPushes, {}] = useMutation(PAUSE_SELECTED_PUSHES);
	const [resumeSelectedPushes, {}] = useMutation(RESUME_SELECTED_PUSHES);
	const [reGenrateExcelFile] = useMutation(FORCE_GENERATE_TIKTOK_PUSH_EXCEL_FILE);
	const [rePush] = useMutation(TIKTOK_REPUSH_PRODUCT_PROCESS);
	let validActions = {
		delete: deleteSelectedPushes,
		pause: pauseSelectedPushes,
		resume: resumeSelectedPushes,
		reGenrate: reGenrateExcelFile,
		rePush: rePush,
	}

	const modalActionLabels = {
		pause: {
			heading: 'Pause this push: #' + (item?.id || ''),
			label: 'Pause this push',
			confirmText: 'Are you sure to pause the selected push?',
		},
		resume: {
			heading: 'Resume this push: #' + (item?.id || ''),
			label: 'Resume this push',
			confirmText: 'Are you sure to resume the selected push?',
		},
		reGenrate: {
			heading: 'Re-generate this push: #' + (item?.id || ''),
			label: 'Re-generate this push',
			confirmText: 'Are you sure to re-generate the selected push?',
		},
		rePush: {
			heading: 'Re-push this push: #' + (item?.id || ''),
			label: 'Push again',
			confirmText: 'Are you sure to push again for the selected push?',
			helpText: "This action only processes products that are in an Error state.",
		},
		delete: {
			heading: 'Delete this push: #' + (item?.id || ''),
			label: 'Delete this push',
			confirmText: 'Are you sure to delete the selected push?',
		},
	}

	return (
		<>
			{renderActions(item)}
			<TiktokProductStore open={state.openDetail} onClose={handleOpenDetail(false)} push={item}/>

			<Modal
				title={modalActionLabels?.[actionInModal?.action]?.heading}
				open={open}
				onClose={handleModalChange}
				primaryAction={{
					content: modalActionLabels?.[actionInModal?.action]?.label,
					loading: modalActionLoading,
					onAction: () => {
						setModalActionLoading(true);

						let selectedItem = actionInModal?.item
						if (selectedItem) {
							switch (actionInModal?.action) {
								case 'pause':
								case 'resume':
								case 'reGenrate':
								case 'rePush':
								case 'delete':
									validActions[actionInModal.action]({
										variables: "reGenrate" === actionInModal.action ?{tiktokPushID: selectedItem.id} :{ ids: [selectedItem.id] },
									})
									.then((res) => {
										setModalActionLoading(false);
										if (res && onActionCompleted) {
											onActionCompleted({
												type: 'success',
												message: _.startCase(actionInModal?.action) + ' processed successfully',
											});
										}
									}).catch((err) => {
										setModalActionLoading(false);
										if (onActionCompleted) {
											onActionCompleted({
												type: 'fail',
												message: _.startCase(actionInModal?.action) + ' failed to process',
											});
										}
									}).finally(() => {
										setOpen(false);
									});
									break;
							}
						}
					}
				}}
				secondaryActions={[
					{
						content: "Cancel",
						onAction: handleModalChange,
					},
				]}
			>
				<Modal.Section>
					<TextContainer>
						<div dangerouslySetInnerHTML={{ __html: modalActionLabels?.[actionInModal.action]?.confirmText }} />
						{modalActionLabels?.[actionInModal.action]?.helpText ? <TextStyle variation="subdued">{modalActionLabels?.[actionInModal.action]?.helpText}</TextStyle> : null}
					</TextContainer>
				</Modal.Section>
			</Modal>
		</>
	);
}

function isAuthor(currentUser, item) {
	const userId = _.get(currentUser, "id")
	const createdByID =  _.get(item, "createdByID")

	return userId === createdByID
}